import '@coreui/coreui-pro/dist/css/coreui.min.css'
import '@coreui/coreui/dist/css/coreui.min.css'
import { CContainer } from '@coreui/react'
import { Backdrop, CircularProgress } from '@mui/material'
import * as Sentry from '@sentry/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { Suspense, useCallback, useEffect, useState } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import AlertComponent from './components/common/AlertComponent/AlertComponent'
import CarbonEmissionsReportComponent from './components/common/CarbonEmissionsReportComponent/CarbonEmissionsReportComponent'
import MultiReportComponent from './components/common/MultiReportComponent/MultiReportComponent'
import ProfileModal from './components/common/ProfileModal/ProfileModal'
import env from './env'
import useCharityLinkQuery from './hooks/links/useCharityLinkQuery'
import useDonationProductQuery from './hooks/products/useDonationProductQuery'
import i18n from './i18n/config'
import MuiThemeProvider from './providers/MuiThemeProvider/MuiThemeProvider'
import routes from './routes'
import './scss/style.scss'
import { getHttpInit } from './utils/api'
import { DEFAULT_ERROR_HANDLER, handleErrorResponse } from './utils/apiUtils'
import { getEmailFromToken, getIsVTigerAdminFromToken } from './utils/commonUtils'
import { useRegion } from './utils/context/RegionContext'

// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))

// Pages
const Login = React.lazy(() => import('./views/pages/login/Login'))
const ResetPasswordPage = React.lazy(() => import('./views/pages/reset-password/ResetPasswordPage'))

function App() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [accounts, setAccounts] = useState([])
  const [viewAccount, setViewAccount] = useState({})
  const [loading, setLoading] = useState(false)
  const [changedRegion, setChangedRegion] = useState(false)
  const [openMultiReport, setOpenMultiReport] = useState(false)
  const [openCarbonReport, setOpenCarbonReport] = useState(false)
  const [openProfile, setOpenProfile] = useState(false)
  const [alertComponent, setAlertComponent] = useState({
    open: false,
    severity: 'warning',
    message: '',
  })

  const [userIsAuthenticated, setUserIsAuthenticated] = useState(
    localStorage.getItem('token') ? true : false,
  )
  const [shouldQueryAccounts, setShouldQueryAccounts] = useState(
    localStorage.getItem('token') ? true : false,
  )
  const [isVTigerAdmin, setIsVTigerAdmin] = useState(() => getIsVTigerAdminFromToken())

  useEffect(() => {
    setIsVTigerAdmin(getIsVTigerAdminFromToken())
  }, [userIsAuthenticated])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const vtigerToken = params.get('vtiger_token')
    if (!vtigerToken) return

    localStorage.setItem('token', vtigerToken)
    params.delete('vtiger_token')
    const newSearch = params.toString()
    navigate(
      { pathname: '/dashboard', search: newSearch ? `?${newSearch}` : '' },
      { replace: true },
    )
    setIsVTigerAdmin(true)
    setUserIsAuthenticated(true)
    setShouldQueryAccounts(true)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const accountList = useQuery({
    retry: false,
    enabled:
      !!shouldQueryAccounts &&
      location.pathname !== '/login' &&
      location.pathname !== '/reset-password' &&
      location.pathname !== '/region',
    queryKey: ['accounts', { email: getEmailFromToken() }],
    queryFn: async ({ queryKey }) => {
      const [, { email }] = queryKey
      const init = getHttpInit('GET')
      const response = await fetch(`${env.API_URL}/api/accounts/${email}/all`, init)
      if (response.ok) {
        return response.json()
      } else {
        handleErrorResponse(response, queryClient, accountList).catch(
          DEFAULT_ERROR_HANDLER(setAlertComponent),
        )

        return undefined
      }
    },
    select: useCallback(
      (data) => {
        if (!data) {
          return []
        }
        const _accounts = [...(data?.Response?.Result?.data?.items || accounts)]
        const numberAccounts = data?.Response?.Result?.data?.count || accounts?.length
        if (!numberAccounts) {
          setAlertComponent({
            open: true,
            severity: 'error',
            message: 'No accounts associated with this email.',
          })
          Logout()
          return []
        }

        const updatedAccountList = _accounts.map((account) => {
          if (!account?.environmentalData?.data) {
            account.totalTrees = 0
            return account
          }
          account.totalTrees = account.environmentalData.data.reduce(
            (total, { trees }) =>
              total +
              trees.reduce((totalTrees, currentValue) => totalTrees + currentValue.value, 0),
            0,
          )
          return account
        })

        const storedAccountId = localStorage.getItem('account')
        if (!storedAccountId) {
          localStorage.setItem('account', updatedAccountList[0].fruitful_account_id)
        }

        shouldQueryAccounts && setShouldQueryAccounts(() => false)
        return updatedAccountList
      },
      [shouldQueryAccounts],
    ),
  })

  useEffect(() => {
    const accountId = localStorage.getItem('account')
    const hasToken = !!localStorage.getItem('token')

    // Only redirect to login when unauthenticated (no token) - prevents loop when
    // token exists but accountId not yet set (e.g. region selection flow)
    if (!hasToken && !accountId && !accountList.isFetched) {
      const isPublicAuthPath =
        location.pathname === '/login' || location.pathname === '/reset-password'
      if (!isPublicAuthPath) navigate({ pathname: '/login', search: location.search })

      return
    }

    if (accountId) {
      let account = {}

      if (accountList.data?.length) {
        const foundAccount = accountList.data.find(
          ({ fruitful_account_id }) => fruitful_account_id === accountId,
        )
        if (foundAccount) {
          account = { ...foundAccount }
          setViewAccount(account)
        } else {
          account = { ...accountList.data[0] }
          localStorage.setItem('account', accountList.data[0].fruitful_account_id)
        }
      } else {
        const fromSessionAccounts = accounts.find(
          ({ fruitful_account_id }) => fruitful_account_id === accountId,
        )
        if (fromSessionAccounts) account = { ...fromSessionAccounts }
        else account.fruitful_account_id = accountId
      }

      setViewAccount(account)
    }
  }, [
    accountList.data,
    accountList.isFetched,
    accounts,
    location.pathname,
    location.search,
    navigate,
  ])

  function handleOfficeChange(
    selectedAccount,
    currentPath = '/dashboard',
    listAccounts = accountList.data || accounts,
  ) {
    const account = listAccounts.find(
      ({ fruitful_account_id }) => fruitful_account_id === selectedAccount,
    )
    if (account) {
      setViewAccount(() => account)
      localStorage.setItem('account', selectedAccount)
      navigate(currentPath === 'workplace' ? '/workplaces' : currentPath)
    }
  }

  const confirmAccountSelection = (accountNo, listOfAccounts = []) => {
    const _accountList = listOfAccounts.length ? listOfAccounts : accountList.data ?? []
    const account = _accountList.find(
      ({ fruitful_account_id }) => fruitful_account_id === accountNo,
    )
    if (account) {
      setViewAccount(() => account)
      localStorage.setItem('account', account.fruitful_account_id)
      navigate('/dashboard')
    }
  }

  const accountWorkplaces = useQuery({
    retry: false,
    enabled: !!viewAccount?.fruitful_account_id && !accountList.isFetching,
    queryKey: ['workplaces', { accountId: viewAccount?.fruitful_account_id }],
    queryFn: async ({ queryKey }) => {
      const [, { accountId }] = queryKey
      const [region, account_id] = accountId.split('-')
      const init = getHttpInit('GET')
      const response = await fetch(
        `${env.API_URL}/api/workplaces?accountId=${account_id}&region=${region}&status=${[
          'Active',
          'Ended',
          'Terminated',
          'NotActive',
        ]}`,
        init,
      )
      if (response.ok) {
        return response.json()
      } else {
        handleErrorResponse(response, queryClient, accountWorkplaces).catch(
          DEFAULT_ERROR_HANDLER(setAlertComponent),
        )

        return undefined
      }
    },
    select: (data) => {
      const workplaces = [...data.Content.Result]
      const activeWorkplaces = workplaces.filter(
        ({ orders, isBroken }) =>
          isBroken || orders.some(({ sostatus }) => ['Active', 'NotActive'].includes(sostatus)),
      )
      const inactiveWorkplaces = workplaces.filter(
        ({ orders, isBroken }) =>
          !isBroken && orders.every(({ sostatus }) => ['Terminated', 'Ended'].includes(sostatus)),
      )
      const activeSortedWorkplaces = activeWorkplaces.sort((workplaceA, workplaceB) =>
        workplaceA.name.localeCompare(workplaceB.name),
      )
      const inactiveSortedWorkplaces = inactiveWorkplaces.sort((workplaceA, workplaceB) =>
        workplaceA.name.localeCompare(workplaceB.name),
      )

      return [...activeSortedWorkplaces, ...inactiveSortedWorkplaces]
    },
  })

  const contactList = useQuery({
    retry: false,
    enabled: !!viewAccount?.fruitful_account_id && !accountList.isFetching,
    queryKey: ['contacts', { accountId: viewAccount?.fruitful_account_id }],
    queryFn: async ({ queryKey }) => {
      const [, { accountId }] = queryKey
      const [region, account_id] = accountId.split('-')
      const init = getHttpInit('GET')
      const response = await fetch(
        `${env.API_URL}/api/accounts/${account_id}/contacts?region=${region}`,
        init,
      )
      if (response.ok) {
        return response.json()
      } else {
        handleErrorResponse(response, queryClient, contactList).catch(
          DEFAULT_ERROR_HANDLER(setAlertComponent),
        )

        return undefined
      }
    },
    onError: (error) => {
      console.log(error)
      throw new Error(JSON.stringify(error))
    },
    select: (data) => {
      return data.Content.Result
    },
  })

  const region = viewAccount?.fruitful_account_id?.split('-')[0]

  const charityLink = useCharityLinkQuery({ region })
  const donationProduct = useDonationProductQuery({ region })

  const Logout = useCallback(async () => {
    localStorage.clear()
    setUserIsAuthenticated(false)
    setIsVTigerAdmin(false)
    setAccounts([])
    setViewAccount({})
    setChangedRegion(false)
    setLoading(false)
    setShouldQueryAccounts(false)

    queryClient.invalidateQueries({ refetchType: 'none' })
    queryClient.clear()
    navigate('/login')
  }, [navigate, queryClient])

  /** @type {Array<TAccount>} */
  const ListOfAccounts = accountList?.data?.length ? accountList.data : accounts

  const myProps = {
    regionId: viewAccount?.fruitful_account_id?.split('-')[0],
    account: viewAccount,
    changedRegion,
    setChangedRegion,
    loading,
    setLoading,
    contactList: contactList.data,
    ListOfAccounts,
    workplaceList: accountWorkplaces.data,
    setAlertComponent,
    confirmAccountSelection,
    Logout: Logout,
    handleOfficeChange,
    accountList,
    setShouldQueryAccounts,
    accountWorkplaces: accountWorkplaces,
    contactsObj: contactList,
    charityLink: charityLink?.data,
    donationProduct: donationProduct?.data,
    setOpenCarbonReport,
    isVTigerAdmin,
  }

  const setBackgroundColor = () => {
    const { pathname } = location

    if (pathname.includes('/invoices/')) return '#ffffff'

    return ''
  }

  const contentChildWrapper = (viewElement) => (
    <CContainer fluid style={{ backgroundColor: setBackgroundColor() }}>
      <Suspense>{viewElement}</Suspense>
    </CContainer>
  )

  const renderContentChild = (viewElement) => (
    <DefaultLayout
      //Header
      logout={Logout}
      accounts={accountList?.data?.length ? accountList.data : accounts}
      viewAccount={viewAccount}
      contactList={contactList.data}
      handleOfficeChange={handleOfficeChange}
      setOpenMultiReport={setOpenMultiReport}
      setOpenCarbonReport={setOpenCarbonReport}
      setOpenProfile={setOpenProfile}
      isVTigerAdmin={isVTigerAdmin}
    >
      {contentChildWrapper(viewElement)}
    </DefaultLayout>
  )
  const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)
  const { urlDefaultLanguage } = useRegion()

  useEffect(() => {
    if (urlDefaultLanguage) {
      i18n.changeLanguage(urlDefaultLanguage)
    }
  }, [urlDefaultLanguage])

  const handleLoginResProps = {
    setAlertComponent,
    setUserIsAuthenticated,
    setAccountList: setAccounts,
    confirmAccountSelection,
    setShouldQueryAccounts,
  }

  return (
    <MuiThemeProvider>
      <Suspense>
        <SentryRoutes>
          <Route path="/login" element={<Login {...handleLoginResProps} />} />
          <Route path="/reset-password" element={<ResetPasswordPage {...handleLoginResProps} />} />
          {routes
            .filter((route) => route.path !== '/login')
            .map((route) => (
              <Route
                key={route.name}
                path={route.path}
                name={route.name}
                exact={route.exact || false}
                element={
                  route.disableWrapping ? (
                    <route.element {...myProps} />
                  ) : (
                    renderContentChild(<route.element {...myProps} />)
                  )
                }
              />
            ))}
        </SentryRoutes>
      </Suspense>
      <MultiReportComponent
        viewAccount={viewAccount}
        contactList={contactList.data}
        openMultiReport={openMultiReport}
        setOpenMultiReport={setOpenMultiReport}
        setAlertComponent={setAlertComponent}
        setLoading={setLoading}
      />
      <CarbonEmissionsReportComponent
        viewAccount={viewAccount}
        contactList={contactList.data}
        openCarbonReport={openCarbonReport}
        setOpenCarbonReport={setOpenCarbonReport}
      />
      <ProfileModal
        viewAccount={viewAccount}
        contactList={contactList.data}
        openProfile={openProfile}
        setOpenProfile={setOpenProfile}
        setAlertComponent={setAlertComponent}
      />
      <Backdrop
        sx={{ color: '#008848', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={
          accountList.isFetching ||
          loading ||
          contactList.isFetching ||
          accountWorkplaces.isFetching
        }
      >
        <CircularProgress color="success" />
      </Backdrop>
      <AlertComponent
        alertComponent={alertComponent}
        alignment={{ vertical: 'top', horizontal: 'center' }}
        setAlertComponent={setAlertComponent}
      />
    </MuiThemeProvider>
  )
}

export default Sentry.withProfiler(App)
